import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { projectsApi, companiesApi, usersApi, ticketsApi } from "../services/api";
import { useFilterMine, useTechnicianLookup } from "../context/FilterMineContext";
import { useAuth } from "../context/AuthContext";
import { attachmentCount, ticketHasAttachments } from "../utils/attachments";
import { useNotification } from "../context/NotificationContext";
import { parseUtc } from "../utils/utcDate";
import { ticketStatusLabel } from "../components/ticketStatusVocabulary";

const STATUS_BADGE = {
  unassigned: "bg-gray-100 text-gray-600",
  open:       "bg-blue-100 text-blue-700",
  in_progress: "bg-yellow-100 text-yellow-700",
  waiting:    "bg-purple-100 text-purple-700",
  closed:     "bg-gray-100 text-gray-400",
};
const TICKET_STATUS_BADGE = {
  open:        "bg-blue-100 text-blue-700",
  in_progress: "bg-yellow-100 text-yellow-700",
  pending:     "bg-purple-100 text-purple-700",
  closed:      "bg-gray-100 text-gray-500",
};
const STATUS_HE    = { unassigned: "לא הוקצה", open: "פתוח", in_progress: "בטיפול", waiting: "המתנה", closed: "סגור" };   // in_progress: set by the backend on first assignment
const PRIORITY_HE  = { low: "נמוכה", medium: "בינונית", high: "גבוהה", critical: "קריטית" };

const EMPTY_FORM = { title: "", company_id: "", assigned_to: "", priority: "medium", status: "open" };

function fmtDate(d) {
  if (!d) return "—";
  return parseUtc(d).toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

export default function ProjectsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { showOnlyMine } = useFilterMine();
  const { user } = useAuth();
  const { technicianId: myTechnicianId, isError: myTechnicianLookupFailed } = useTechnicianLookup();
  const { addNotification } = useNotification();

  const assignedToFilter = showOnlyMine && user?.id ? { assigned_to: user.id } : {};
  const techFilter = showOnlyMine && myTechnicianId ? { technician_id: myTechnicianId } : {};
  // "Only mine" with the technician id not known yet (list loading / failed): the request went out WITHOUT the filter and the page
  // listed everybody's queue tickets under a "mine only" label. Hold the query instead (same as TicketsPage / Dashboard).
  const minePending = showOnlyMine && !myTechnicianId;

  const { data: projects = [], isLoading, isError: projectsFailed } = useQuery({
    queryKey: ["projects", assignedToFilter],
    queryFn: () => projectsApi.list({ limit: 500, ...assignedToFilter }),
  });
  const { data: linkedTickets = [], isLoading: isLoadingTickets, isError: linkedFailed } = useQuery({
    queryKey: ["tickets-project-queue", techFilter],
    queryFn: () => ticketsApi.list({ queue_type: "project", limit: 500, ...techFilter }),
    enabled: !minePending,
  });
  const { data: companies = [] } = useQuery({ queryKey: ["companies"], queryFn: () => companiesApi.list() });
  const { data: users = [] }     = useQuery({ queryKey: ["users"],     queryFn: () => usersApi.list() });

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const createProject = useMutation({
    mutationFn: (data) => projectsApi.create(data),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      setShowForm(false);
      setForm(EMPTY_FORM);
      navigate(`/projects/${created.id}`);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה ביצירת פרויקט", "error"),
  });

  const rows = Array.isArray(projects) ? projects : (projects.items ?? []);

  const merged = [
    ...rows.map((p) => ({
      _type: "project", id: p.id,
      title: p.title, company: p.company,
      person: p.assignee?.display_name || "—",
      statusBadge: STATUS_BADGE[p.status] || "bg-gray-100 text-gray-600",
      statusLabel: STATUS_HE[p.status] || p.status,
      progress: p.progress ?? 0,
      updated_at: p.updated_at,
    })),
    ...linkedTickets.map((t) => ({
      _type: "ticket", id: t.id,
      title: t.title, company: t.company,
      person: t.sender_name,
      statusBadge: TICKET_STATUS_BADGE[t.status] || "bg-gray-100 text-gray-500",
      statusLabel: ticketStatusLabel(t.status),
      progress: null,
      updated_at: t.updated_at,
      ticket_number: t.ticket_number, has_reply: t.has_customer_reply,
      attachments_count: t.attachments_count,
      has_attachments: t.has_attachments,
      hasAttachments: t.hasAttachments,
    })),
  ].sort((a, b) => parseUtc(b.updated_at) - parseUtc(a.updated_at));

  return (
    <div className="space-y-4" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">📁 פרויקטים</h1>
          {!isLoading && !isLoadingTickets && (
            <span className="text-sm text-gray-400">{merged.length} פריטים</span>
          )}
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-purple-600 hover:bg-purple-700 text-white text-sm px-4 py-2 rounded-lg transition-colors"
        >
          + פרויקט חדש
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="bg-white rounded-xl border p-5 space-y-4">
          <h3 className="font-semibold text-gray-800">פרויקט חדש</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">כותרת *</label>
              <input
                autoFocus
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="שם הפרויקט"
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">חברה</label>
              <select value={form.company_id} onChange={(e) => setForm((f) => ({ ...f, company_id: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">—</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">אחראי</label>
              <select value={form.assigned_to} onChange={(e) => setForm((f) => ({ ...f, assigned_to: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">—</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.display_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">עדיפות</label>
              <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                {Object.entries(PRIORITY_HE).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">סטטוס</label>
              <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                {Object.entries(STATUS_HE).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50">ביטול</button>
            <button
              onClick={() => {
                if (!form.title.trim()) return;
                createProject.mutate({
                  title: form.title.trim(),
                  company_id: form.company_id || null,
                  assigned_to: form.assigned_to || null,
                  priority: form.priority,
                  status: form.status,
                });
              }}
              disabled={!form.title.trim() || createProject.isPending}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
            >
              {createProject.isPending ? "יוצר..." : "צור פרויקט"}
            </button>
          </div>
        </div>
      )}

      {rows.length >= 500 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          מוצגים 500 הפרויקטים האחרונים בלבד — ייתכן שפרויקטים ישנים יותר אינם מופיעים.
        </p>
      )}
      {linkedTickets.length >= 500 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          מוצגים 500 כרטיסי התור האחרונים בלבד — ייתכן שכרטיסים ישנים יותר אינם מופיעים.
        </p>
      )}
      {/* merged.length === 0 below already covers a TOTAL failure with a full error screen; this
          covers the PARTIAL case (one of the two lists failed while the other still has data), which
          used to render the table with no indication anything was missing at all. */}
      {(projectsFailed || linkedFailed) && merged.length > 0 && (
        <p role="alert" className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {projectsFailed && linkedFailed
            ? "טעינת הפרויקטים וכרטיסי התור נכשלה — הרשימה עשויה להיות חלקית."
            : projectsFailed
            ? "טעינת הפרויקטים נכשלה — מוצגים רק כרטיסי התור."
            : "טעינת כרטיסי התור נכשלה — מוצגים רק הפרויקטים."}
        </p>
      )}
      {/* Unified table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        {(isLoading || isLoadingTickets) ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
          </div>
        ) : merged.length === 0 && (projectsFailed || linkedFailed) ? (
          // a failed request is not "no projects"
          <div role="alert" className="text-center py-16 text-red-600">
            <p className="font-medium">לא ניתן לטעון את הפרויקטים</p>
          </div>
        ) : merged.length === 0 && showOnlyMine && myTechnicianLookupFailed ? (
          // a failed technician-identity lookup used to look the same as "no projects assigned to me"
          <div role="alert" className="text-center py-16 text-red-600">
            <p className="font-medium">לא ניתן לאמת את זהות הטכנאי — לא ניתן להציג "רק שלי" כרגע</p>
          </div>
        ) : merged.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <p className="text-4xl mb-3">📁</p>
            <p>אין פרויקטים עדיין</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50 text-gray-600 text-xs">
                <th className="text-right px-4 py-3 font-medium">#</th>
                <th className="text-right px-4 py-3 font-medium">כותרת</th>
                <th className="text-right px-4 py-3 font-medium">חברה</th>
                <th className="text-right px-4 py-3 font-medium">אחראי / שולח</th>
                <th className="text-right px-4 py-3 font-medium">סטטוס</th>
                <th className="text-right px-4 py-3 font-medium">סוג</th>
                <th className="text-right px-4 py-3 font-medium">התקדמות</th>
                <th className="text-right px-4 py-3 font-medium">עדכון</th>
              </tr>
            </thead>
            <tbody>
              {merged.map((row, idx) => (
                <tr
                  key={`${row._type}-${row.id}`}
                  onClick={() => navigate(row._type === "project" ? `/projects/${row.id}` : `/tickets/${row.id}`)}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter" && e.key !== " ") return;
                    e.preventDefault();
                    navigate(row._type === "project" ? `/projects/${row.id}` : `/tickets/${row.id}`);
                  }}
                  tabIndex={0}
                  role="button"
                  className="border-b last:border-0 hover:bg-purple-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 text-gray-400 text-xs font-mono">
                    <div className="inline-flex items-center gap-1 whitespace-nowrap">
                      <span>{row._type === "ticket" ? `#${row.ticket_number}` : idx + 1}</span>
                      {row._type === "ticket" && ticketHasAttachments(row) && (
                        <span title={`${attachmentCount(row)} קבצים מצורפים`} className="inline-flex items-center gap-0.5 text-slate-400 text-sm leading-none flex-shrink-0">
                          📎
                          <span className="text-[10px] font-semibold leading-none">{attachmentCount(row)}</span>
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900">
                    <div className="flex items-center gap-1.5">
                      {row.title}
                      {row.has_reply && <span className="text-orange-500 text-sm leading-none">✉</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{row.company?.name || "—"}</td>
                  <td className="px-4 py-3 text-gray-600">{row.person}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${row.statusBadge}`}>
                      {row.statusLabel}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${row._type === "project" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>
                      {row._type === "project" ? "פרויקט" : "קריאה"}
                    </span>
                  </td>
                  <td className="px-4 py-3 w-28">
                    {row.progress !== null ? (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                          <div className="bg-purple-500 rounded-full h-1.5" style={{ width: `${row.progress}%` }} />
                        </div>
                        <span className="text-xs text-gray-500 w-8 text-left">{row.progress}%</span>
                      </div>
                    ) : (
                      <span className="text-gray-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{fmtDate(row.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
